import { isRecord } from './guards.ts'
import { validateTheme, type Theme } from './theme.ts'

export type OriginsLookup =
  | { kind: 'found'; origins: string[]; theme?: Theme }
  | { kind: 'unknown' }
  | { kind: 'error' }

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1'])
const ORIGINS_PATH = '/v1/terminal/terminals'

/**
 * Checks that a value is an exact https origin, or http on localhost when allowed.
 * @param value - The candidate origin.
 * @param allowLocalHttp - Whether http on localhost or 127.0.0.1 is accepted.
 * @returns True when the value is one exact origin.
 */
export function isAllowedOrigin(
  value: unknown,
  allowLocalHttp: boolean
): boolean {
  if (typeof value !== 'string' || value.includes('*')) return false
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.origin !== value) return false
  if (url.protocol === 'https:') return true
  return (
    allowLocalHttp && url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname)
  )
}

/**
 * Deployed terminal hosts, including aliases, must not enable local http.
 * @param protocol - `location.protocol`, including the colon.
 * @param hostname - `location.hostname`.
 * @returns True only for http on localhost or 127.0.0.1.
 */
export function pageAllowsLocalHttp(
  protocol: string,
  hostname: string
): boolean {
  return protocol === 'http:' && LOCAL_HOSTS.has(hostname)
}

export interface LookupTerminalOriginsConfig {
  apiBase: string
  terminalId: string
  allowLocalHttp: boolean
  fetch?: typeof fetch
  timeoutMs?: number
}

function sendsPreviewCookie(apiBase: string): boolean {
  let url: URL
  try {
    url = new URL(apiBase)
  } catch {
    return false
  }
  return url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname)
}

function themeFrom(body: Record<string, unknown>): Theme | undefined {
  if (!('theme' in body)) return undefined
  const result = validateTheme(body.theme)
  return result.ok ? result.theme : undefined
}

/**
 * Reads one terminal's allowed parent origins and its display theme.
 * A missing or invalid theme leaves the origins result unchanged.
 * @param config - The API origin, the terminal id, and the local-http rule.
 * @returns The filtered origins, unknown when the terminal has none, or error when the lookup fails.
 */
export async function lookupTerminalOrigins(
  config: LookupTerminalOriginsConfig
): Promise<OriginsLookup> {
  const fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis)
  const timeoutMs = config.timeoutMs ?? 5_000
  let response: Response
  try {
    response = await fetchImpl(
      `${config.apiBase}${ORIGINS_PATH}/${encodeURIComponent(config.terminalId)}`,
      {
        headers: { accept: 'application/json' },
        // The local mock keeps each preview on a cookie. Deployed CORS does not allow credentials.
        credentials: sendsPreviewCookie(config.apiBase)
          ? 'include'
          : 'same-origin',
        signal: AbortSignal.timeout(timeoutMs)
      }
    )
  } catch {
    return { kind: 'error' }
  }
  if (response.status === 404) return { kind: 'unknown' }
  if (!response.ok) return { kind: 'error' }
  const body: unknown = await response.json().catch(() => null)
  if (!isRecord(body) || !Array.isArray(body.allowedOrigins)) {
    return { kind: 'error' }
  }
  const origins = body.allowedOrigins.filter((origin): origin is string =>
    isAllowedOrigin(origin, config.allowLocalHttp)
  )
  if (origins.length === 0) return { kind: 'unknown' }
  const theme = themeFrom(body)
  return theme === undefined
    ? { kind: 'found', origins }
    : { kind: 'found', origins, theme }
}

/**
 * Checks every frame above the terminal, where the browser lists them. Firefox does not.
 * @param ancestors - `location.ancestorOrigins`, parent first, or undefined.
 * @param allowed - The terminal's allowed parent origins.
 * @returns False when any listed ancestor is outside the allowed origins. A masked `null` entry is skipped.
 */
export function ancestorsAllowed(
  ancestors: ArrayLike<string> | undefined,
  allowed: readonly string[]
): boolean {
  if (!ancestors) return true
  return Array.from(ancestors).every(
    origin => origin === 'null' || allowed.includes(origin)
  )
}
